import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { InviteAdminRequestSchema, type InviteAdminRequest } from '@condo/contracts';
import { useInviteAdmin } from './api';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

export function InviteModal({
  tenantId,
  open,
  onOpenChange,
}: {
  tenantId: string;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const { register, handleSubmit, formState, reset } = useForm<InviteAdminRequest>({
    resolver: zodResolver(InviteAdminRequestSchema),
  });
  const mutation = useInviteAdmin(tenantId);

  const onSubmit = handleSubmit(async (data) => {
    try {
      const res = await mutation.mutateAsync(data);
      toast.success(`Administrador agregado: ${res.email}`);
      reset();
      onOpenChange(false);
    } catch (err: unknown) {
      const apiErr = err as { status?: number; detail?: string } | null;
      if (apiErr?.status === 409) {
        toast.error('Este correo ya es administrador de este condominio.');
      } else {
        toast.error(apiErr?.detail ?? 'No se pudo enviar la invitación.');
      }
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invitar administrador</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <Label htmlFor="email">Correo</Label>
            <Input id="email" type="email" {...register('email')} />
            {formState.errors.email && (
              <p className="mt-1 text-xs text-[var(--color-danger)]">
                {formState.errors.email.message}
              </p>
            )}
          </div>
          <div>
            <Label htmlFor="displayName">Nombre (opcional)</Label>
            <Input id="displayName" {...register('displayName')} />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Enviando…' : 'Enviar invitación'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
